import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  IconCashBanknoteMove,
  IconDotsVertical,
  IconFileText,
  IconInfoCircle,
  IconUser,
  IconUsers,
} from "@tabler/icons-react";
import moment from "moment/moment";
import { Table } from "../UI/Table";
import Status from "../UI/Status";
import Text from "../UI/Text";
import Tooltip from "../UI/Tooltip";
import DropdownMenu from "../UI/DropdownMenu";
import EmptyState from "../UI/EmptyState";
// @ts-expect-error pure js
import IsLoading from "../UI/IsLoading";
import { Selectable } from "../UI/Selectable";
import FilterBar from "../UI/FilterBar/FilterBar";
import FilterSection from "../UI/FilterBar/FilterSection";
// @ts-expect-error pure js
import useProfileBailleurs from "../../hooks/use-profile-bailleurs";
import {
  useMandatsGestion,
  useSoldesMandants,
} from "../../hooks/use-compta-pro";
// @ts-expect-error pure js
import { currencyFormatter } from "../../api/Functions";
import FichierVirementModal, {
  type VirementLigne,
} from "../Compta/FichierVirementModal";
import BulkCrgModal from "../Profile/BulkCrgModal";
import type { ProfileBailleur } from "../Profile/types";
import type { MandatGestion } from "../../models/comptabilite";
import styles from "./BailleursTab.module.css";

type MandatStatut = "actif" | "termine" | "hors_mandat";

const MANDAT_OPTIONS: { value: MandatStatut; label: string }[] = [
  { value: "actif", label: "Mandat actif" },
  { value: "termine", label: "Mandat terminé" },
  { value: "hors_mandat", label: "Hors mandat" },
];

const SOLDE_OPTIONS = [
  { value: "positif", label: "Solde à reverser" },
  { value: "nul", label: "Solde nul ou débiteur" },
];

const MANDAT_STATUS: Record<
  MandatStatut,
  { color: "success" | "neutral" | "warning"; text: string }
> = {
  actif: { color: "success", text: "Mandat actif" },
  termine: { color: "neutral", text: "Mandat terminé" },
  hors_mandat: { color: "warning", text: "Hors mandat" },
};

function estActif(mandat: MandatGestion): boolean {
  return (
    mandat.date_fin === null ||
    moment(mandat.date_fin).isSameOrAfter(moment(), "day")
  );
}

function mandatStatut(mandats: MandatGestion[]): MandatStatut {
  if (mandats.length === 0) return "hors_mandat";
  return mandats.some(estActif) ? "actif" : "termine";
}

function numeroMandat(mandats: MandatGestion[]): string | null {
  const mandat = mandats.find(estActif) ?? mandats[0];
  if (!mandat) return null;
  return mandat.mandate_number || String(mandat.numero);
}

interface ActionPayload {
  profileIds: number[];
  resetSelected?: () => void;
}

export default function BailleursTab() {
  const navigate = useNavigate();
  const { profileBailleurs, isLoading } = useProfileBailleurs() as {
    profileBailleurs: ProfileBailleur[];
    isLoading: boolean;
  };
  const { mandats } = useMandatsGestion();
  const { data: soldesMandants } = useSoldesMandants();

  const [search, setSearch] = useState("");
  const [filterMandat, setFilterMandat] = useState<string[]>([]);
  const [filterSolde, setFilterSolde] = useState<string[]>([]);
  const [crgPayload, setCrgPayload] = useState<ActionPayload | null>(null);
  const [virementPayload, setVirementPayload] = useState<ActionPayload | null>(
    null,
  );

  const mandatsByProfile = useMemo(() => {
    const byProfile = new Map<number, MandatGestion[]>();
    mandats.forEach((mandat) => {
      byProfile.set(mandat.id_profile_bailleur, [
        ...(byProfile.get(mandat.id_profile_bailleur) ?? []),
        mandat,
      ]);
    });
    return byProfile;
  }, [mandats]);

  // Solde présent pour les seuls profils sous mandat : les autres n'ont pas de compte mandant.
  const soldes = useMemo(() => {
    const byProfile = new Map<number, number>();
    (soldesMandants ?? []).forEach((solde) => {
      byProfile.set(solde.id_profile_bailleur, parseFloat(solde.solde));
    });
    return byProfile;
  }, [soldesMandants]);

  const filteredProfiles = useMemo(() => {
    const searchLower = search.toLowerCase();
    return profileBailleurs.filter((profile) => {
      if (
        searchLower &&
        !profile.nom_profile.toLowerCase().includes(searchLower)
      ) {
        return false;
      }
      const statut = mandatStatut(mandatsByProfile.get(profile.id) ?? []);
      if (filterMandat.length > 0 && !filterMandat.includes(statut)) {
        return false;
      }
      if (filterSolde.length > 0) {
        const positif = (soldes.get(profile.id) ?? 0) > 0;
        if (!filterSolde.includes(positif ? "positif" : "nul")) {
          return false;
        }
      }
      return true;
    });
  }, [
    profileBailleurs,
    search,
    filterMandat,
    filterSolde,
    mandatsByProfile,
    soldes,
  ]);

  function profilesOf(payload: ActionPayload | null): ProfileBailleur[] {
    return (payload?.profileIds ?? [])
      .map((id) => profileBailleurs.find((profile) => profile.id === id))
      .filter((profile): profile is ProfileBailleur => Boolean(profile));
  }

  const virementLignes: VirementLigne[] = profilesOf(virementPayload).map(
    (profile) => ({
      idProfileBailleur: profile.id,
      nom: profile.nom_profile,
      solde: soldes.get(profile.id) ?? 0,
      ibanManquant: !profile.iban || !profile.bic,
    }),
  );

  const activeFiltersCount =
    (filterMandat.length > 0 ? 1 : 0) + (filterSolde.length > 0 ? 1 : 0);

  if (isLoading) {
    return <IsLoading />;
  }

  if (profileBailleurs.length === 0) {
    return (
      <EmptyState
        icon={IconUsers}
        title="Aucun bailleur"
        description="Créez un profil bailleur pour chaque mandant dont vous gérez les biens."
      />
    );
  }

  return (
    <div className={styles.stack}>
      <BulkCrgModal
        open={crgPayload !== null}
        onOpenChange={(open) => {
          if (!open) setCrgPayload(null);
        }}
        profiles={profilesOf(crgPayload)}
        onGenerated={() => crgPayload?.resetSelected?.()}
      />
      <FichierVirementModal
        open={virementPayload !== null}
        onOpenChange={(open) => {
          if (!open) setVirementPayload(null);
        }}
        lignes={virementLignes}
        onGenerated={() => virementPayload?.resetSelected?.()}
      />
      <Selectable items={filteredProfiles}>
        <FilterBar
          searchValue={search}
          onSearchChange={setSearch}
          searchPlaceholder="Rechercher un bailleur"
          activeFiltersCount={activeFiltersCount}
          onClearFilters={() => {
            setFilterMandat([]);
            setFilterSolde([]);
          }}
          leftActions={
            <>
              <Selectable.ActionButton
                icon={IconFileText}
                onClick={(
                  selectedItems: (number | string)[],
                  resetSelected: () => void,
                ) =>
                  setCrgPayload({
                    profileIds: selectedItems as number[],
                    resetSelected,
                  })
                }
              >
                Comptes rendus de gestion
              </Selectable.ActionButton>
              <Selectable.ActionButton
                variant="secondary"
                icon={IconCashBanknoteMove}
                onClick={(
                  selectedItems: (number | string)[],
                  resetSelected: () => void,
                ) =>
                  setVirementPayload({
                    profileIds: selectedItems as number[],
                    resetSelected,
                  })
                }
              >
                Fichier de virement
              </Selectable.ActionButton>
            </>
          }
        >
          <FilterSection
            title="Mandat"
            options={MANDAT_OPTIONS}
            values={filterMandat}
            onChange={setFilterMandat}
            defaultOpen
          />
          <FilterSection
            title="Solde"
            options={SOLDE_OPTIONS}
            values={filterSolde}
            onChange={setFilterSolde}
          />
        </FilterBar>
        <Table variant="card">
          <Table.Header>
            <Table.Column>
              <Selectable.Checkbox selectAll />
            </Table.Column>
            <Table.Column>Bailleur</Table.Column>
            <Table.Column>Mandat</Table.Column>
            <Table.Column align="right">
              <Tooltip
                content="Solde du compte du mandant, diminué des loyers appelés mais non encaissés : c'est le montant repris dans le fichier de virement."
                direction="bottom"
              >
                <Text as="span" className={styles.soldeHeader}>
                  Solde comptable <IconInfoCircle size={16} />
                </Text>
              </Tooltip>
            </Table.Column>
            <Table.Column align="right">Actions</Table.Column>
          </Table.Header>
          <Table.Body>
            {filteredProfiles.map((profile, index) => {
              const mandatsProfile = mandatsByProfile.get(profile.id) ?? [];
              const statut = mandatStatut(mandatsProfile);
              const numero = numeroMandat(mandatsProfile);
              const solde = soldes.get(profile.id);
              return (
                <Table.Row
                  key={profile.id}
                  className={styles.clickableRow}
                  onClick={() => navigate(`/bailleur/profiles/${profile.id}`)}
                >
                  <Table.Cell onClick={(e) => e.stopPropagation()}>
                    <Selectable.Checkbox value={profile.id} index={index} />
                  </Table.Cell>
                  <Table.Cell>
                    <Text as="span" weight="bold">
                      {profile.nom_profile}
                    </Text>
                  </Table.Cell>
                  <Table.Cell>
                    <div className={styles.mandatCell}>
                      <Status
                        color={MANDAT_STATUS[statut].color}
                        text={MANDAT_STATUS[statut].text}
                      />
                      {numero && <Text as="span">n°{numero}</Text>}
                    </div>
                  </Table.Cell>
                  <Table.Cell align="right">
                    {solde === undefined ? (
                      <Text as="span" color="muted">
                        —
                      </Text>
                    ) : (
                      <Text
                        as="span"
                        weight="bold"
                        color={solde > 0 ? "success" : "neutral"}
                      >
                        {currencyFormatter(solde, { withSign: true })}
                      </Text>
                    )}
                  </Table.Cell>
                  <Table.Cell align="right">
                    <DropdownMenu
                      icon={IconDotsVertical}
                      label="Actions du bailleur"
                      align="end"
                      className={styles.action}
                    >
                      <DropdownMenu.Item
                        icon={IconUser}
                        label="Voir le profil"
                        onClick={() =>
                          navigate(`/bailleur/profiles/${profile.id}`)
                        }
                      />
                      <DropdownMenu.Item
                        icon={IconFileText}
                        label="Compte rendu de gestion"
                        onClick={() =>
                          setCrgPayload({ profileIds: [profile.id] })
                        }
                      />
                      {statut !== "hors_mandat" && (
                        <DropdownMenu.Item
                          icon={IconCashBanknoteMove}
                          label="Fichier de virement"
                          onClick={() =>
                            setVirementPayload({ profileIds: [profile.id] })
                          }
                        />
                      )}
                    </DropdownMenu>
                  </Table.Cell>
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table>
      </Selectable>
    </div>
  );
}
